import React from "react";
import { Badge } from "../../ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui/tooltip";
import { Info } from "lucide-react";

/**
 * ColliMetricsBadge — herbruikbare weergave van CBM + gewicht + chargeable.
 *
 * Regel 2: LEEST UITSLUITEND uit backend-response. Berekent zelf niets.
 * Verwacht een `metrics` object dat door de backend is berekend
 * (bv. uit `platform_core.colli.compute_colli_totals`).
 *
 * Fields (alle optioneel — wordt niet getoond wanneer null/0):
 *  - total_pieces
 *  - total_cbm
 *  - total_actual_weight
 *  - total_volumetric_weight
 *  - total_chargeable_weight
 *  - total_value, currency
 *  - transport_mode
 */
export default function ColliMetricsBadge({ metrics, compact = false, testId = "colli-metrics" }) {
  if (!metrics) return null;
  const {
    total_pieces,
    total_cbm,
    total_actual_weight,
    total_volumetric_weight,
    total_chargeable_weight,
    total_value,
    currency,
    transport_mode,
  } = metrics;

  const fmtNum = (n, digits = 2) =>
    typeof n === "number"
      ? n.toLocaleString("nl-NL", { minimumFractionDigits: digits, maximumFractionDigits: digits })
      : "—";

  const fmtCurrency = (n) =>
    typeof n === "number"
      ? `${currency || "EUR"} ${n.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : "—";

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid={testId}>
        {total_pieces > 0 && (
          <Badge variant="secondary" data-testid={`${testId}-pieces`}>
            {fmtNum(total_pieces, 0)} stuks
          </Badge>
        )}
        {total_cbm > 0 && (
          <Badge variant="secondary" data-testid={`${testId}-cbm`}>
            CBM {fmtNum(total_cbm, 4)}
          </Badge>
        )}
        {total_chargeable_weight > 0 && (
          <Badge variant="secondary" data-testid={`${testId}-chargeable`}>
            {fmtNum(total_chargeable_weight, 2)} kg
          </Badge>
        )}
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4"
      data-testid={testId}
    >
      <MetricCell label="Aantal colli" value={fmtNum(total_pieces, 0)} testId={`${testId}-pieces`} />
      <MetricCell label="Totaal CBM" value={fmtNum(total_cbm, 4)} unit="m³" testId={`${testId}-cbm`} />
      <MetricCell
        label="Werkelijk gewicht"
        value={fmtNum(total_actual_weight, 2)}
        unit="kg"
        testId={`${testId}-actual`}
      />
      <MetricCell
        label="Vol. gewicht"
        value={fmtNum(total_volumetric_weight, 2)}
        unit="kg"
        info={
          transport_mode
            ? `Berekend voor transport-mode "${transport_mode}"`
            : "Berekend via backend platform_core.colli"
        }
        testId={`${testId}-volumetric`}
      />
      <MetricCell
        label="Chargeable"
        value={fmtNum(total_chargeable_weight, 2)}
        unit="kg"
        highlight
        info="Grootste van werkelijk of volumetrisch gewicht"
        testId={`${testId}-chargeable`}
      />
      {typeof total_value === "number" && total_value > 0 && (
        <MetricCell
          label="Totale waarde"
          value={fmtCurrency(total_value)}
          testId={`${testId}-value`}
        />
      )}
    </div>
  );
}

function MetricCell({ label, value, unit, info, highlight, testId }) {
  return (
    <div
      className={`rounded-md ${highlight ? "bg-white border border-slate-300 shadow-sm" : ""} p-2`}
      data-testid={testId}
    >
      <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-slate-500">
        {label}
        {info && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-3 w-3 items-center justify-center"
                  aria-label="Toelichting"
                >
                  <Info className="h-3 w-3 text-slate-400" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">{info}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className="text-base font-semibold text-slate-900">
        {value}
        {unit && <span className="ml-1 text-xs font-normal text-slate-500">{unit}</span>}
      </div>
    </div>
  );
}
